import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { SegmentedToggle } from '@/components/sales-report/components/SegmentedToggle'
import { MONO, fmt } from './format'
import { PC_VIEWS, lineTerm, type Group, type LineRef, type PcView, type SplitFig } from './classification'

/** "· 5 disciplines" / "· 2 stages" / "· 3 categories" beside a department name. */
export function childHint(group: Group<unknown>, view: PcView): string {
  const n = group.children.length
  const noun = group.stageable ? (view === 'Stage' ? 'stage' : 'discipline') : 'category'
  return `· ${n} ${n === 1 ? noun : noun === 'category' ? 'categories' : `${noun}s`}`
}

/** Planning Center + Lab split by category — Gross worked, PC credit absorbed and
 *  True Sale per line. Planning Center shows its five disciplines or, with the
 *  Stage toggle, the same total by stage; Lab shows Design / Printing / Milling;
 *  the Unclassified remainder is its own line, so the departments sum to the
 *  total. Click a line for its breakdown. */
export function CategorySplitCard({
  groups,
  total,
  view,
  onViewChange,
  onSelect,
  currency,
  periodLabel,
  className,
}: {
  groups: Group<SplitFig>[] | null
  total: SplitFig
  view: PcView
  onViewChange: (v: PcView) => void
  onSelect: (ref: LineRef) => void
  currency: string
  periodLabel: string
  className?: string
}) {
  const credit = (v: number) => (v ? '−' + fmt(v, currency) : '—')

  const figures = (f: SplitFig, strong: boolean) => (
    <>
      <td className={cn('px-3 py-3 text-right', strong ? 'font-semibold' : 'text-stone-500 dark:text-stone-400', MONO)}>{fmt(f.gross, currency)}</td>
      <td className={cn('px-3 py-3 text-right text-red-600 dark:text-red-400', strong && 'font-semibold', MONO)}>{credit(f.pcCredit)}</td>
      <td className={cn('px-5 py-3 text-right font-semibold', strong && 'font-bold', MONO)}>{fmt(f.trueSale, currency)}</td>
    </>
  )

  const lineRow = (ref: LineRef, value: SplitFig, level: 'child' | 'department') => (
    <tr
      key={`${ref.kind}:${ref.id}`}
      data-line={ref.id}
      onClick={() => onSelect(ref)}
      className="cursor-pointer border-b border-stone-50 hover:bg-champagne-50/70 dark:border-stone-800/60 dark:hover:bg-champagne-500/10"
    >
      <td className="px-5 py-3">
        <span className={cn('flex items-center gap-1.5', level === 'child' ? 'pl-5 text-stone-700 dark:text-stone-200' : 'font-medium text-stone-800 dark:text-stone-100')}>
          {level === 'child' && <span className="h-1 w-1 shrink-0 rounded-full bg-stone-300 dark:bg-stone-600" aria-hidden />}
          <span>{ref.name}</span>
          <span onClick={(e) => e.stopPropagation()} className="inline-flex">
            <InfoPopover term={lineTerm(ref)} />
          </span>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-stone-300 dark:text-stone-600" aria-hidden />
        </span>
      </td>
      {figures(value, false)}
    </tr>
  )

  return (
    <div className={className}>
      <div className="flex flex-wrap items-start justify-between gap-2 px-5 pt-5">
        <div>
          <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
            By category · {periodLabel}
            <InfoPopover term="category" />
          </h3>
          <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            Gross worked, PC credit absorbed, and the True Sale that brings in new cash · click a category for its breakdown
          </p>
        </div>
        {groups && (
          <span className="inline-flex items-center gap-1">
            <SegmentedToggle options={PC_VIEWS} value={view} onChange={onViewChange} size="sm" ariaLabel="Planning Center split by discipline or stage" />
            <InfoPopover term="stage" />
          </span>
        )}
      </div>
      {groups ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm" data-testid="category-split">
            <thead>
              <tr className="border-y border-stone-100 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-800 dark:text-stone-500">
                <th className="px-5 py-3 text-left font-medium">{view === 'Stage' ? 'Department › Stage / Category' : 'Department › Category'}</th>
                <th className="px-3 py-3 text-right font-medium">Gross</th>
                <th className="px-3 py-3 text-right font-medium">PC credit</th>
                <th className="px-5 py-3 text-right font-semibold text-stone-600 dark:text-stone-300">True Sale</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) =>
                g.line ? (
                  lineRow(g.line, g.subtotal, 'department')
                ) : (
                  [
                    <tr key={`dept:${g.id}`} data-department={g.id} className="border-b border-stone-100 bg-stone-50/70 dark:border-stone-800 dark:bg-stone-800/40">
                      <td className="px-5 py-3 font-semibold text-stone-800 dark:text-stone-100">
                        <span className="inline-flex items-center gap-1">
                          {g.name}
                          <span className="text-[11px] font-normal text-stone-400 dark:text-stone-500">{childHint(g, view)}</span>
                          {g.info && <InfoPopover term={g.info} />}
                        </span>
                      </td>
                      {figures(g.subtotal, true)}
                    </tr>,
                    ...g.children.map((c) => lineRow(c.ref, c.value, 'child')),
                  ]
                ),
              )}
              <tr className="border-t-2 border-stone-200 dark:border-stone-700">
                <td className="px-5 py-3.5 font-semibold">Total</td>
                <td className={cn('px-3 py-3.5 text-right font-bold', MONO)}>{fmt(total.gross, currency)}</td>
                <td className={cn('px-3 py-3.5 text-right font-bold text-red-600 dark:text-red-400', MONO)}>{credit(total.pcCredit)}</td>
                <td className={cn('px-5 py-3.5 text-right font-bold', MONO)}>{fmt(total.trueSale, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-5 pb-5 pt-3 text-sm text-stone-500 dark:text-stone-400">
          This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see the split by category.
        </p>
      )}
    </div>
  )
}
